import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { lcApiBase, lcHttpError } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

/** Contraintes logo (alignées backend + affichage POS 32×32). */
export const TYPE_MODE_LOGO_CONSTRAINTS = {
  maxBytes: 200 * 1024,
  /** Source max avant resize serveur → 128×128. */
  maxPx: 512,
  accept: 'image/png,image/jpeg,image/jpg,image/webp,image/gif',
  hint: 'PNG / JPG / WebP / GIF — max 200 Ko, idéalement carré ≤ 128×128 px (affiché 32×32 en caisse). Source max 512×512.',
} as const;

export interface TypeModePaiment {
  id: number;
  libelle: string;
  is_sodexo?: number | boolean;
  is_manuelle?: number | boolean;
  affectation?: string | null;
  type?: string | null;
  active?: number | boolean;
  mode?: string | null;
  logo?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface TypeModePaimentPayload {
  libelle: string;
  is_sodexo?: boolean;
  is_manuelle?: boolean;
  affectation?: string | null;
  type?: string | null;
  active?: boolean;
  mode?: string | null;
  logo?: string | null;
  remove_logo?: boolean;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseTypeModePaimentService {
  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  private base(): string {
    return `${lcApiBase(this.authService)}/api/types-mode-paiement`;
  }

  logoUrl(logo: string | null | undefined): string | null {
    const raw = String(logo || '').trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
    const base = lcApiBase(this.authService).replace(/\/$/, '');
    return raw.startsWith('/') ? `${base}${raw}` : `${base}/${raw}`;
  }

  initialLetter(libelle: string | null | undefined): string {
    const s = String(libelle || '')
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const ch = s.charAt(0);
    return ch ? ch.toUpperCase() : '?';
  }

  getAll(options?: { activeOnly?: boolean }): Observable<TypeModePaiment[]> {
    const params: Record<string, string> = {};
    if (options?.activeOnly) {
      params['active'] = '1';
    }
    return this.http.get<{ success: boolean; data?: TypeModePaiment[]; error?: string }>(this.base(), { params }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Impossible de charger les modes de paiement');
        }
        return Array.isArray(res.data) ? res.data : [];
      }),
      catchError((err) => throwError(() => lcHttpError(err, 'Impossible de charger les modes de paiement')))
    );
  }

  create(payload: TypeModePaimentPayload, logoFile?: File | null): Observable<TypeModePaiment> {
    const body = logoFile ? this.toFormData(payload, logoFile) : payload;
    return this.http.post<{ success: boolean; data?: TypeModePaiment; error?: string }>(this.base(), body).pipe(
      map((res) => {
        if (!res?.success || !res.data) {
          throw new Error(res?.error || 'Création impossible');
        }
        return res.data;
      }),
      catchError((err) => throwError(() => lcHttpError(err, 'Création impossible')))
    );
  }

  update(
    id: number,
    payload: Partial<TypeModePaimentPayload>,
    logoFile?: File | null
  ): Observable<TypeModePaiment> {
    const body = logoFile || payload.remove_logo ? this.toFormData(payload, logoFile) : payload;
    return this.http
      .put<{ success: boolean; data?: TypeModePaiment; error?: string }>(`${this.base()}/${id}`, body)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Mise à jour impossible');
          }
          return res.data;
        }),
        catchError((err) => throwError(() => lcHttpError(err, 'Mise à jour impossible')))
      );
  }

  deactivate(id: number): Observable<TypeModePaiment> {
    return this.http
      .delete<{ success: boolean; data?: TypeModePaiment; error?: string }>(`${this.base()}/${id}`)
      .pipe(
        map((res) => {
          if (!res?.success || !res.data) {
            throw new Error(res?.error || 'Désactivation impossible');
          }
          return res.data;
        }),
        catchError((err) => throwError(() => lcHttpError(err, 'Désactivation impossible')))
      );
  }

  private toFormData(payload: Partial<TypeModePaimentPayload>, logoFile?: File | null): FormData {
    const fd = new FormData();
    const entries: [string, unknown][] = [
      ['libelle', payload.libelle],
      ['is_sodexo', payload.is_sodexo],
      ['is_manuelle', payload.is_manuelle],
      ['affectation', payload.affectation],
      ['type', payload.type],
      ['active', payload.active],
      ['mode', payload.mode],
    ];
    for (const [key, val] of entries) {
      if (val === undefined) continue;
      if (val === null) {
        fd.append(key, '');
        continue;
      }
      if (typeof val === 'boolean') {
        fd.append(key, val ? '1' : '0');
        continue;
      }
      fd.append(key, String(val));
    }
    if (payload.remove_logo) {
      fd.append('remove_logo', '1');
    }
    if (logoFile) {
      fd.append('logo', logoFile, logoFile.name);
    }
    return fd;
  }
}
